import { Box } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { StorageLimitIcon } from 'assets/icons'
import {
  DisplayModeIcon,
  MenuFirstDayIcon,
  MenuLanguageIcon
} from 'assets/icons/menu'
import { PlanName } from 'components/dialogs/payment/PlansInfoDialog'
import StorageProgressLoader from 'components/loaders/StorageProgressLoader'
import PopupSection from 'components/popup/PopupSection'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import {
  APP_THEME_MODES,
  AppThemeMode,
  useAppSettings
} from 'components/providers/AppSettingsProvider'
import { usePayment } from 'components/providers/PaymentProvider'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getAccountStorage } from 'services/api/account'
import { updateUserSettings } from 'services/api/userSettings'
import Database from 'services/db/Database'
import { AccountDoc, UserSettingsDoc } from 'types/api/changes'
import { Model } from 'types/model'
import { highestActiveTier, Tier } from 'utils/api/accounts'

import PickLanguagePopup, { langs } from '../PickLanguagePopup'
import SelectItemWithSearchPopup from '../SelectItemWithSearchPopup'
import DefaultCalendarSection from './DefaultCalendarSection'
import { days } from './UserMenuPopup'

export interface SettingsSectionProps {
  account?: Model<AccountDoc>
  userSettings: Model<UserSettingsDoc> | undefined
  setMenuOpen: (b: boolean) => void
}

function nextRecommendedPlan(currentTier: Tier): PlanName | null {
  switch (currentTier) {
    case Tier.FREE:
      return 'tier1'
    case Tier.PRO:
      return 'tier2'
    case Tier.BUSINESS:
      return 'tier3'
    case Tier.BUSINESS_PLUS:
      return 'tier4'
    default:
      return null
  }
}

export default function SettingsSection(props: SettingsSectionProps) {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const appSettings = useAppSettings()
  const { showPaymentDialog } = usePayment()

  const [showLanguagePopup, onShowLanguagePopup] = useState(false)
  const [showFirstDayPopup, onShowFirstDayPopup] = useState(false)
  const [showThemePopup, onShowThemePopup] = useState(false)
  const [isStorageLoading, setIsStorageLoading] = useState(true)
  const [totalPhotos, setTotalPhotos] = useState(0)
  const [photoQuotaLimit, setPhotoQuotaLimit] = useState(0)

  const accentColor = theme.palette.primary.main

  const usageRatio = useMemo(() => {
    if (photoQuotaLimit <= 0) return 0
    return totalPhotos / photoQuotaLimit
  }, [photoQuotaLimit, totalPhotos])

  const usagePercent = Math.max(0, Math.min(100, usageRatio * 100))
  const isNearQuota = photoQuotaLimit > 0 && usageRatio >= 0.9
  const storageLeadingOffset = i18n.dir() === 'ltr' ? '32px' : 0
  const storageValue = `${totalPhotos}/${
    photoQuotaLimit > 0 ? photoQuotaLimit : t('unlimited')
  }`

  const activeTier = highestActiveTier(props.account)
  const recommendedPlan = nextRecommendedPlan(activeTier)

  const showLangauge = () => {
    onShowLanguagePopup(true)
  }

  const onLangChanged = (lang: string) => {
    window.location.reload()
    i18n.changeLanguage(lang)
  }

  const showFirstDay = () => {
    onShowFirstDayPopup(true)
  }

  const showThemeSelector = () => {
    onShowThemePopup(true)
  }

  const themeOptions = [
    { id: APP_THEME_MODES.SYSTEM, title: t('system') },
    { id: APP_THEME_MODES.LIGHT, title: t('light') },
    { id: APP_THEME_MODES.DARK, title: t('dark') }
  ]

  return (
    <PopupSection title={`${t('settings')}`} popupSectionGap={1}>
      <PickLanguagePopup
        open={showLanguagePopup}
        setOpen={onShowLanguagePopup}
        selectItem={onLangChanged}
      >
        <span onClick={showLangauge}>
          <PopupSectionRow
            cursor="pointer"
            title={`${t('language')}`}
            iconClass="icon-regular"
            svgHeight="20px"
            maxWidthIcon={20}
            actionBg
            engulfEntireRow
            icon={<MenuLanguageIcon style={{ cursor: 'default' }} />}
            value={
              langs.find((lang) => i18n.language.includes(lang.lang))?.name
            }
            valueColor={theme.palette.text.disabled}
          />
        </span>
      </PickLanguagePopup>
      <SelectItemWithSearchPopup
        open={showFirstDayPopup}
        setOpen={onShowFirstDayPopup}
        selectItem={(item) => {
          const userSettings = Database.toServerObject<UserSettingsDoc>(
            props.userSettings
          ) as UserSettingsDoc
          userSettings.FirstDayOfTheWeek = Number(item.id ?? 1)
          updateUserSettings(userSettings).then(() => {
            appSettings.setFirstDayOfTheWeek(userSettings.FirstDayOfTheWeek)
            props.setMenuOpen(false)
          })
        }}
        data={days}
        selectedItemId={`${appSettings.firstDayOfTheWeek}`}
      >
        <span id="first-day-button" onClick={showFirstDay}>
          <PopupSectionRow
            title={`${t('firstDay')}`}
            iconClass="icon-regular"
            maxWidthIcon={20}
            cursor="pointer"
            actionBg
            engulfEntireRow
            icon={<MenuFirstDayIcon style={{ cursor: 'default' }} />}
            value={
              days.find((day) => day.id === `${appSettings.firstDayOfTheWeek}`)
                ?.title
            }
            valueColor={theme.palette.text.disabled}
          />
        </span>
      </SelectItemWithSearchPopup>
      {props.account && (
        <DefaultCalendarSection {...props} account={props.account} />
      )}
      <SelectItemWithSearchPopup
        open={showThemePopup}
        setOpen={onShowThemePopup}
        selectItem={(item) => {
          onShowThemePopup(false)
          props.setMenuOpen(false)
          const mode = item.id as AppThemeMode

          if (
            mode === APP_THEME_MODES.SYSTEM ||
            mode === APP_THEME_MODES.LIGHT ||
            mode === APP_THEME_MODES.DARK
          ) {
            appSettings.setThemeMode(mode)
          }
        }}
        data={themeOptions}
        selectedItemId={appSettings.themeMode}
      >
        <span onClick={showThemeSelector}>
          <PopupSectionRow
            title={`${t('theme')}`}
            iconClass="icon-regular"
            maxWidthIcon={20}
            cursor="pointer"
            actionBg
            engulfEntireRow
            icon={<DisplayModeIcon style={{ cursor: 'default' }} />}
            value={
              themeOptions.find((item) => item.id === appSettings.themeMode)
                ?.title
            }
            valueColor={theme.palette.text.disabled}
          />
        </span>
      </SelectItemWithSearchPopup>
    </PopupSection>
  )
}
